import React, { useState, useRef, useCallback, memo } from 'react';
import type { FilterSearchProps } from '../FilterPanel.types';

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="7" cy="7" r="4" />
    <path d="M10 10l3.5 3.5" strokeLinecap="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 3l8 8M11 3l-8 8" strokeLinecap="round" />
  </svg>
);

function FilterSearchComponent({ config, value, onChange, compact }: FilterSearchProps): React.ReactElement {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Debounce the onChange callback
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, 300);

    // Update input immediately for responsiveness
    if (inputRef.current) {
      inputRef.current.value = newValue;
    }
  }, [onChange]);

  const handleClear = () => {
    onChange('');
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 'var(--text-xs)',
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--space-1)'
      }}>
        {config.label}
      </label>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}>
        <span style={{
          position: 'absolute',
          left: 'var(--space-3)',
          color: isFocused ? 'var(--color-primary)' : 'var(--color-text-tertiary)',
          transition: 'color var(--transition-fast)',
          pointerEvents: 'none'
        }}>
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="text"
          defaultValue={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={config.placeholder || 'Ara...'}
          style={{
            width: '100%',
            padding: compact
              ? 'var(--space-2) var(--space-8)'
              : 'var(--space-2) var(--space-8)',
            paddingRight: value ? 'var(--space-8)' : 'var(--space-3)',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-body)',
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${isFocused ? 'var(--color-primary)' : 'var(--color-border-default)'}`,
            background: 'var(--color-bg-elevated)',
            color: 'var(--color-text-primary)',
            outline: 'none',
            transition: 'all var(--transition-fast)'
          }}
        />
        {value && (
          <button
            onClick={handleClear}
            aria-label="Aramayı temizle"
            style={{
              position: 'absolute',
              right: 'var(--space-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              padding: 0,
              border: 'none',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-neutral-200)',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-neutral-300)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-neutral-200)';
            }}
          >
            <ClearIcon />
          </button>
        )}
      </div>
    </div>
  );
}

export const FilterSearch = memo(FilterSearchComponent);
